/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetInferenceSpendCapsRequest,
  GetInferenceSpendCapsSecurity,
} from "../models/operations/getinferencespendcaps.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetInferenceSpendCapsQuery,
  GetInferenceSpendCapsQueryData,
  prefetchGetInferenceSpendCaps,
  queryKeyGetInferenceSpendCaps,
} from "./getInferenceSpendCaps.core.js";
export {
  buildGetInferenceSpendCapsQuery,
  type GetInferenceSpendCapsQueryData,
  prefetchGetInferenceSpendCaps,
  queryKeyGetInferenceSpendCaps,
};

export type GetInferenceSpendCapsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getInferenceSpendCaps usage
 *
 * @remarks
 * List current usage and caps for the organization's materialized platform-managed inference keys
 */
export function useGetInferenceSpendCaps(
  request?: GetInferenceSpendCapsRequest | undefined,
  security?: GetInferenceSpendCapsSecurity | undefined,
  options?: QueryHookOptions<
    GetInferenceSpendCapsQueryData,
    GetInferenceSpendCapsQueryError
  >,
): UseQueryResult<
  GetInferenceSpendCapsQueryData,
  GetInferenceSpendCapsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildGetInferenceSpendCapsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getInferenceSpendCaps usage
 *
 * @remarks
 * List current usage and caps for the organization's materialized platform-managed inference keys
 */
export function useGetInferenceSpendCapsSuspense(
  request?: GetInferenceSpendCapsRequest | undefined,
  security?: GetInferenceSpendCapsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetInferenceSpendCapsQueryData,
    GetInferenceSpendCapsQueryError
  >,
): UseSuspenseQueryResult<
  GetInferenceSpendCapsQueryData,
  GetInferenceSpendCapsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetInferenceSpendCapsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetInferenceSpendCapsData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: GetInferenceSpendCapsQueryData,
): GetInferenceSpendCapsQueryData | undefined {
  const key = queryKeyGetInferenceSpendCaps(...queryKeyBase);

  return client.setQueryData<GetInferenceSpendCapsQueryData>(key, data);
}

export function invalidateGetInferenceSpendCaps(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "usage",
      "getInferenceSpendCaps",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGetInferenceSpendCaps(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getInferenceSpendCaps"],
  });
}
